import React, { useMemo, useState, useEffect } from 'react';
import { AccessibilityProps } from './subComponents/AccessibilityLink/AccessibilityLink';
import {
  BannerContainer,
  BannerContainerProps,
} from './subComponents/BannerContainer/BannerContainer';
import Link from '../Link/Link';
import parseJSONstring from '../helpers/parseJSONstring';
import useDeviceWidth from '../helpers/useDeviceWidth';
import BannerText from './subComponents/BannerText/BannerText';

export interface GlobalBannerCommonProps
  extends AccessibilityProps,
    BannerContainerProps {
  backgroundColor?: 'red' | 'white' | 'black';
  bannerText?: string;
  linkableText?: string;
  optOutSlug?: string;
  textColor?: 'red' | 'white' | 'black';
}

export type GlobalBannerProps = React.ComponentPropsWithoutRef<'div'> & {
  className?: string;
  index?: number;
} & GlobalBannerCommonProps;

type OptOutArrayProps = {
  title: string;
  href: string;
};

export const shouldHideBannerContent = (
  optOutSlug: string,
  pathname: string
) => {
  if (optOutSlug) {
    const optOutArray: OptOutArrayProps[] = parseJSONstring(optOutSlug);

    if (Array.isArray(optOutArray)) {
      const optOutPaths = optOutArray.map((optOutObject) => {
        return optOutObject.href;
      });
      return optOutPaths.includes(pathname);
    }
    return false;
  }
  return false;
};

function GlobalBanner({
  backgroundColor,
  bannerText,
  children,
  className,
  linkableText = '',
  optOutSlug = '',
  textColor,
  title,
  uid,
  index,
}: GlobalBannerProps) {
  const bannerDefaultProps = {
    uid,
    title,
    className,
    index,
    bannerText,
  };

  const newStyle = useMemo<React.CSSProperties>(() => {
    return { backgroundColor, color: textColor };
  }, [backgroundColor, textColor]);

  const linkableTextInfo = parseJSONstring(linkableText);
  const hideBannerContent = shouldHideBannerContent(
    optOutSlug,
    window.location.pathname + window.location.search
  );
  const { href: linkableURL, ...restOfLinkableData } = linkableTextInfo;
  const isCheckoutPage = window.location.pathname.startsWith('/checkout');
  const isConfirmationPage =
    window.location.pathname.includes('order-confirmation');
  const size = useDeviceWidth();
  const [isMobile, setIsMobile] = useState(false);
  const isCheckoutSubSection = isCheckoutPage && !isConfirmationPage;
  const showBannerContent = isCheckoutSubSection ? false : !hideBannerContent;

  useEffect(() => {
    setIsMobile(size === 'mobile');
  }, [size]);

  return (
    <BannerContainer
      {...bannerDefaultProps}
      showBannerContent={showBannerContent}
      style={newStyle}
    >
      <BannerText text={bannerText} isMobile={isMobile} />
      {linkableURL && (
        <Link
          url={linkableURL}
          {...restOfLinkableData}
          theme={backgroundColor === 'white' ? 'dark' : 'light'}
        />
      )}
      {children}
    </BannerContainer>
  );
}

export default GlobalBanner;
